import { queryOptions } from '@tanstack/react-query'
import type { KambiSportName } from '@mobi/api-types/kambi'
import { getEventGroupsFromRoot, getGroupHighlights } from '../api/groups'
import { sportsOfferingQueryKeys } from './keys'
import {
  getKambiBetOffersByIds,
  getKambiBetOffersByEventIds,
  getKambiBetOffersByGroupIds,
} from '../api/betOffers'
import {
  getEventsBySportTermKey,
  getEventsForGroupId,
  getUpcomingEventsForAllSports,
} from '../api/events'
import { getKambiEventsForCompetition, getFuturesEventsForCompetition } from '../api/listView'
import { getPreMatchCategoryForSport } from '../api/categories'
import { getHasSGMForEvent, getHasSGMForGroup } from '../sgmAvailability'
import { getKambiEventPricing } from '../api/onDemandPricing'

export const getEventGroupsFromRootQuery = queryOptions({
  queryFn: getEventGroupsFromRoot,
  queryKey: sportsOfferingQueryKeys.EventGroupsFromRoot,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
})

export const getMatchesBySportTermKeyQuery = (sport: string) =>
  queryOptions({
    queryFn: () => getEventsBySportTermKey(sport),
    queryKey: sportsOfferingQueryKeys.EventsForSport(sport),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getGroupHighlightsQuery = queryOptions({
  queryFn: getGroupHighlights,
  queryKey: sportsOfferingQueryKeys.EventGroupHighlights,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
})

export const getUpcomingEventsForAllSportsQuery = queryOptions({
  queryFn: getUpcomingEventsForAllSports,
  queryKey: sportsOfferingQueryKeys.UpcomingEventsForAllSports,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
})

export const getPreMatchCategoryForSportQuery = (sport: KambiSportName | undefined) =>
  queryOptions({
    queryFn: () => (sport ? getPreMatchCategoryForSport(sport) : undefined),
    queryKey: ['sport', 'pre-match-category', sport],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2,
    enabled: !!sport,
  })

export const getKambiBetOffersByIdsQuery = (betOfferIds: number[]) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByIds({ betOfferIds }),
    queryKey: sportsOfferingQueryKeys.BetOffersByIds(betOfferIds),
    staleTime: 1_000 * 60 * 10, // 10min
  })

export const getKambiBetOffersByEventIdQuery = (eventId: number) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByEventIds({ eventIds: [eventId] }),
    queryKey: sportsOfferingQueryKeys.BetOffersByEvents([eventId]),
    refetchOnWindowFocus: false,
  })

export const getKambiBetOffersByGroupIdQuery = (groupId: number) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByGroupIds({ groupIds: [groupId] }),
    queryKey: sportsOfferingQueryKeys.BetOffersByGroups([groupId]),
    refetchOnWindowFocus: false,
  })

export const getKambiBetOffersByEventIdsQuery = (eventIds: number[]) =>
  queryOptions({
    queryFn: () =>
      eventIds.length !== 0 ? getKambiBetOffersByEventIds({ eventIds }) : Promise.resolve(null),
    queryKey: sportsOfferingQueryKeys.BetOffersByEvents(eventIds),
    staleTime: 1_000 * 60 * 10, // 10min
  })

export const getMatchesForCompetitionQuery = (sport: string, region: string, competition: string) =>
  queryOptions({
    queryFn: () => getKambiEventsForCompetition(sport, region, competition, 'all'),
    queryKey: [sportsOfferingQueryKeys.EventsForCompetition(sport, region, competition), 'matches'],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getFuturesMatchesForCompetitionQuery = (
  sport: string,
  region: string,
  competition: string
) =>
  queryOptions({
    queryFn: () => getFuturesEventsForCompetition(sport, region, competition),
    queryKey: [sportsOfferingQueryKeys.EventsForCompetition(sport, region, competition), 'futures'],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const eventsForCompetitionIdQuery = (competitionId: number) =>
  queryOptions({
    queryFn: () => getEventsForGroupId(competitionId),
    queryKey: sportsOfferingQueryKeys.EventsForGroupId(competitionId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getHasSGMForGroupQuery = (groupId: number) =>
  queryOptions({
    queryFn: () => getHasSGMForGroup(groupId),
    queryKey: sportsOfferingQueryKeys.SgmAvailabilityForGroup(groupId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getHasSGMForEventQuery = (eventId: number) =>
  queryOptions({
    queryFn: () => getHasSGMForEvent(eventId),
    queryKey: sportsOfferingQueryKeys.SgmAvailabilityForEvent(eventId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getKambiEventPricingQuery = (eventId: number) =>
  queryOptions({
    queryFn: () => getKambiEventPricing(eventId),
    queryKey: sportsOfferingQueryKeys.EventPricingForEvent(eventId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })
